<script>
import { apiVoz } from '@modules/aawz-intelligence-v2/api/apiVoz'
import { criarFalanteComoAudio } from '@modules/aawz-intelligence-v2/utils/avatar-ao-vivo/falante-como-audio'
import { CONTENT_TYPE_PCM } from '@modules/aawz-intelligence-v2/utils/avatar-ao-vivo/protocolo'
import {
  BOAS_VINDAS,
  ROTA_PLANOS,
  blocosDoRoteiro,
  partesComLink,
  semEmojis,
  textoFaladoDoRoteiro,
} from '@modules/aawz-intelligence-v2/components/boas-vindas-falada/roteiro'
import {
  blocoNoProgresso,
  pesosDosBlocos,
  progressoDoAudio,
} from '@modules/aawz-intelligence-v2/components/boas-vindas-falada/sincronia'
import {
  PAPEL,
  estruturarRoteiro,
} from '@modules/aawz-intelligence-v2/components/boas-vindas-falada/estrutura-do-roteiro'
import {
  CANAL_AUDIO,
  CANAL_TEXTO,
} from '@modules/aawz-intelligence-v2/api/apiBoasVindas'
import { boasVindasFaladaMidiaMixin } from './boas-vindas-falada-midia.mixin'
import BoasVindasPasso from './boas-vindas-passo.vue'

// O PLAYER da boas-vindas falada (spec-009 [US-2], decisão D-009-1).
//
// ★ É um player de MENSAGEM FALADA, não um "vídeo que fala", e isso foi medido, não
// escolhido: a API do Viggle não faz um personagem recitar um texto — `lip.?sync|tts|
// voice|speech|audio` dá ZERO ocorrência na OpenAPI inteira (`~/aawz-tmp/viggle-sonda/
// ACHADO.md`). O que existe é retrato/clipe + o TTS que já roda em produção + legenda. O
// clipe é opcional POR CONSTRUÇÃO: sem ele o player mostra o retrato; sem os dois, mostra
// só o roteiro, e continua cumprindo a US-2 inteira.
//
// ★ O TEXTO ESTÁ NA TELA DESDE O PRIMEIRO FRAME (AC-2.6). Ele não espera a síntese, não
// espera o `play()` e não some se o áudio falhar — a falta do áudio nunca deixa o primeiro
// contato mudo. O áudio, quando chega, apenas ACENDE o bloco que está sendo dito. É por
// isso que não há estado de "carregando" no lugar do roteiro: não há nada a carregar para
// a mensagem existir.
//
// ★ O NOME VEM DE FORA (AC-2.3, o N21 da spec-008): `primeiroNome` é prop, lida da SESSÃO
// pelo host — nunca do modelo. Sem nome, a saudação sai sem nome; nunca um `<nome>` ou um
// "consultor" inventado (`roteiro.js::saudacao`).
//
// ★ AUTOPLAY. O browser só toca áudio depois de um gesto, e a síntese leva segundos — a
// ativação transitória do envio da mensagem já expirou quando o `play()` acontece. Quando
// ele é recusado, isso NÃO é falha: o aviso vira um convite ("Toque em Ouvir") e o
// consultor decide. Tratar a recusa como erro assustaria com um problema que não existe.
//
// ★ O QUE NÃO ESTÁ AQUI: quem decide QUANDO mostrar a boas-vindas (a primeira mensagem da
// vida do profissional, AC-2.1), quem marca isso no servidor e o registro do consumo da
// síntese (AC-2.7) são do host e do backend. Este componente recebe, toca e avisa quando
// terminou (`@fim`) — é o gancho da US-3 para oferecer o modo de voz.
const TIPO_PADRAO = 'audio/mpeg'

// Teto da espera pela sessão do avatar. Ver `prepararFala`: o texto já está
// na tela, então o que este prazo atrasa é a VOZ, não a mensagem. 3 s cobre o
// caso medido (1,0-2,6 s ocioso) sem deixar alguém olhando para um roteiro
// mudo quando o provedor está fora do ar.
const TETO_ESPERAR_AVATAR_MS = 3000

export default {
  name: 'BoasVindasFalada',
  components: { BoasVindasPasso },
  mixins: [boasVindasFaladaMidiaMixin],
  props: {
    // O PRIMEIRO nome, vindo da sessão. Vazio é um estado válido e previsto.
    primeiroNome: {
      type: String,
      default: '',
    },
    // Clipe de fundo, quando o host já tem a URL na mão. Opcional.
    clipe: {
      type: String,
      default: '',
    },
    // ★ O provedor de clipe (`provedor-de-clipe.js`), quando o host prefere delegar a
    // busca. Opcional, e resolvido SEM ESPERA: o player monta sem clipe e, se ele chegar,
    // entra. É isto que garante que o vendor externo nunca fique no caminho crítico do
    // primeiro contato — por construção, e não porque cada host lembrou de não esperar.
    provedorDeClipe: {
      type: Object,
      default: null,
    },
    // ★ AVATAR AO VIVO (spec-009): quem FALA esta apresentação, quando há um
    // rosto ao vivo pronto para falá-la.
    //
    // Contrato: `{prontoEm(ms) -> Promise<boolean>, falar(pcm) -> Promise,
    // interromper()}`. Ausente — que é o caso de toda superfície que não é o
    // primeiro contato — este componente é BYTE A BYTE o de antes: sintetiza
    // MP3 e toca num `<audio>` próprio.
    //
    // O owner pediu o avatar *"só para as boas vindas e para ter a conversa
    // inicial"* (18/09/2026). Sem isto, o rosto só entrava DEPOIS da
    // apresentação — e no instante em que ele olhava, a tela mostrava o clipe
    // de fundo, não o rosto.
    falante: {
      type: Object,
      default: null,
    },
    // Retrato estático — usado quando não há clipe. Também opcional.
    retrato: {
      type: String,
      default: '',
    },
    // Voz do catálogo curado (spec-016). Vazio deixa o backend escolher a padrão.
    vozId: {
      type: String,
      default: '',
    },
    // Não pedir a síntese. O host liga quando já sabe que o áudio não deve tocar (uma
    // reabertura, por exemplo) — e a mensagem continua inteira, escrita.
    /**
     * **EXPERIMENTO de estilo (18/09/2026, a pedido do owner) — feito para ser
     * desligado num único lugar.**
     *
     * `false` (default) = como sempre foi: o cartão com moldura
     * (`az-card-bordered pa-6`) e os quatro passos sobre `--neutral-50`.
     *
     * `true` = **sem moldura**: nenhuma borda, nenhum fundo, nenhum padding
     * próprio — só o texto no corpo da conversa. Para os passos 1-4 não
     * sumirem contra o branco, eles trocam de tom (`--white` com hairline),
     * que é o contraste invertido do desenho de hoje.
     *
     * ⚠️ Voltar é trocar esta prop de volta para `false` no chamador
     * (`ui-registry/cartoes/cartao-boas-vindas.vue`), sem tocar em CSS: o
     * bloco `--sem-moldura` do `<style>` é aditivo e não altera o default.
     */
    semMoldura: {
      type: Boolean,
      default: false,
    },
    semAudio: {
      type: Boolean,
      default: false,
    },
    /**
     * A apresentação já é HISTÓRICO — vieram turnos depois dela.
     *
     * ★ Derivado do THREAD (`messages.vue` → moldura → `cartao-boas-vindas`),
     * nunca de estado local: `avancou` é `data` e morre na remontagem, então
     * numa conversa reaberta o "Continuar" voltava a aparecer e o clique não
     * levava a lugar nenhum. É o mesmo sinal, e o mesmo motivo, do
     * `etapaHistorica` dos cartões de etapa do onboarding.
     */
    etapaHistorica: {
      type: Boolean,
      default: false,
    },
    /**
     * ★ [owner, 21/09/2026] A apresentação já foi AVANÇADA — o thread guarda a
     * resposta da pessoa logo depois dela (o "Continuar", ou a resposta da
     * etapa 1 que chega no mesmo turno). É o `avancou` lido do THREAD, e
     * existe porque `avancou` é `data`: a cópia do aside nascia com o
     * "Continuar" de pé enquanto a bolha já o tinha escondido (*"no aside
     * isso não reflete, o botão continua lá"*). Chega pela moldura
     * (`resposta-do-cartao` → `cartao-boas-vindas.vue`).
     */
    respondidaNoThread: {
      type: Boolean,
      default: false,
    },
    // ★ [18/09/2026] A mensagem JÁ FOI ENTREGUE — o player colapsa e continua na tela.
    //
    // O owner testou em `/home-ia` e cobrou: *"a mensagem de boas vindas saiu, quero que
    // fique"*. Até aqui o host desmontava o player no `@fim`, e a apresentação da casa
    // evaporava — a conversa passava a começar na pergunta dele.
    //
    // ★ COLAPSAM as AÇÕES, nunca o TEXTO: "Continuar" sai (não há o que continuar) e entra
    // a linha de entregue mais o convite para reouvir. O roteiro fica inteiro, porque é ele
    // que o owner quis manter — e porque uma seção que nasce fechada esconde justamente o
    // que a pessoa acabou de receber.
    concluida: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      avancou: false,
      copy: BOAS_VINDAS,
      // -1 é "nenhum bloco aceso" — o estado antes de a fala começar.
      blocoAtivo: -1,
      tocando: false,
      // Já houve reprodução? Separa "Ouvir" (primeira vez) de "Continuar ouvindo".
      comecouAFalar: false,
      aviso: '',
      terminou: false,
      // ★ O canal já foi reportado? `entregue` sai UMA vez: ele é o que vira o registro
      // no servidor, e dois eventos virariam duas marcações contando a mesma entrega.
      canalReportado: false,
      // A síntese está em voo. O botão fica em loading enquanto isso: sem ele, um clique
      // em "Ouvir" antes de o áudio existir seria um clique que não faz nada — o oposto
      // do que a plataforma faz com ação bloqueada.
      preparando: false,
      // A URL que o provedor entregou depois de o player já estar na tela. Vazia em
      // tudo que não é clipe pronto: gerando, indisponível e ausente caem no mesmo lugar.
      clipeResolvido: '',
      // ★ Espelho REATIVO de `this._audio`. O elemento em si fica fora do `data()` (ver
      // `montarAudio`), e um computed que dependesse dele NÃO recomputaria — Vue 2 não
      // observa o que não declarou. Este booleano é o que a tela pode ler.
      temAudio: false,
    }
  },
  computed: {
    /**
     * A moldura do cartão — ou a ausência dela (ver a prop `semMoldura`).
     *
     * ⚠️ `ia-block-card`, nunca `az-card-bordered`: este cartão divide o thread
     * com os cartões de etapa do onboarding, e as duas classes divergiam em
     * borda, raio e sombra. O desempate está medido em
     * `design/_ia-block-card.sass` — sobre `--ia-content-bg` o hairline dá
     * 1.149 de razão contra 1.287 da borda da família.
     *
     * `pa-6` fica: `.ia-block-card` não traz padding próprio.
     */
    classesDaMoldura() {
      if (this.semMoldura) return 'boas-vindas-falada--sem-moldura'
      return 'ia-block-card pa-6'
    },
    blocos() {
      return blocosDoRoteiro(this.primeiroNome)
    },
    pesos() {
      return pesosDosBlocos(this.blocos)
    },
    /**
     * Os blocos prontos para pintar — com o caminho de créditos virado LINK.
     *
     * ⚠️ Computed e não função no template: o `v-for` reavalia a cada re-render, e
     * `partesComLink` percorre o bloco inteiro. Aqui o custo é pago uma vez por mudança
     * de `blocos`, que é o que a regra do repo pede.
     *
     * `destino` é `null` na esmagadora maioria dos blocos — a tela pinta texto puro.
     */
    blocosNaTela() {
      const rota = { name: ROTA_PLANOS }
      return this.blocos.map((bloco) => {
        const partes = partesComLink(bloco)
        // `texto` vai SEMPRE, com ou sem link: é ele que os blocos sem link pintam, e
        // deixá-lo de fora no bloco do caminho faria um bloco em branco no dia em que o
        // link mudar de parágrafo.
        return partes
          ? { texto: bloco, ...partes, destino: rota }
          : { texto: bloco, destino: null }
      })
    },
    /**
     * Os blocos com o PAPEL de cada um no desenho (`estrutura-do-roteiro.js`).
     *
     * Separado de `estruturaDoRoteiro` porque só depende de `blocos`: o `blocoAtivo` muda
     * a cada `timeupdate` do áudio, e reparsear os quatro passos quatro vezes por segundo
     * seria trabalho jogado fora.
     */
    papeisDoRoteiro() {
      return estruturarRoteiro(this.blocos)
    },
    /**
     * O que a tela pinta: papel, partes do link e o destaque da legenda, já resolvidos.
     *
     * ★ `ativo`/`dataAtivo` vêm pré-computados porque o template não decide nada — regra
     * do repo, e aqui ela evita `indice === blocoAtivo` repetido em seis lugares.
     */
    estruturaDoRoteiro() {
      return this.papeisDoRoteiro.map((item) => {
        const ativo = item.indice === this.blocoAtivo
        const naTela = this.blocosNaTela[item.indice]
        return {
          ...item,
          ...naTela,
          ativo,
          dataAtivo: ativo ? 'true' : 'false',
          // ★ A TELA também não pinta emoji (owner, 18/09/2026: o 🚀 sai do fechamento).
          // É a MESMA `semEmojis` que a síntese usa — o `roteiro.js` fica intocado,
          // porque ele é o contrato com o `BRIEF.md` da spec.
          texto: semEmojis(naTela.texto),
          antes: semEmojis(naTela.antes),
          depois: semEmojis(naTela.depois),
        }
      })
    },
    saudacaoDoRoteiro() {
      return this.doPapel(PAPEL.SAUDACAO)[0] || null
    },
    paragrafosDoRoteiro() {
      return this.doPapel(PAPEL.PARAGRAFO)
    },
    rotuloDosPassos() {
      return this.doPapel(PAPEL.ROTULO)[0] || null
    },
    passosDoRoteiro() {
      return this.doPapel(PAPEL.PASSO)
    },
    linhasDoRodape() {
      return this.doPapel(PAPEL.LINHA)
    },
    /**
     * O "Continuar" aparece enquanto a apresentação ainda não foi avançada.
     *
     * ⚠️ Some depois do clique e na mensagem HISTÓRICA (`etapaHistorica`): ele
     * é um passo do roteiro, não um botão permanente — um "Continuar" parado
     * numa mensagem antiga convida a um clique que não leva a lugar nenhum.
     *
     * ⚠️ NÃO é `concluida` que o esconde — ver o comentário dentro do computed.
     */
    mostrarBotaoDeAvancar() {
      // ⚠️ NÃO usar `concluida` aqui — foi o bug de 18/09/2026, e o motivo é
      // sutil: com `sem-audio` a entrega é reportada já na MONTAGEM
      // (`reportarEntrega(CANAL_TEXTO)` no `created`), então `concluida`
      // nasce `true` e o botão nunca aparecia. `concluida` quer dizer "a
      // entrega foi registrada", não "a pessoa terminou de ler".
      //
      // ★ `etapaHistorica` é o que faltava: `avancou` é `data`, morre na
      // remontagem, e numa conversa REABERTA o botão voltava a aparecer — um
      // "Continuar" parado numa mensagem antiga, que é exatamente o que o
      // parágrafo acima promete evitar. O veredito vem do THREAD, não de
      // estado local.
      return !this.avancou && !this.etapaHistorica && !this.respondidaNoThread
    },
    fechamentoDoRoteiro() {
      return this.doPapel(PAPEL.FECHAMENTO)[0] || null
    },
    rotuloDaFala() {
      if (this.tocando) return BOAS_VINDAS.PAUSAR
      if (this.concluida) return BOAS_VINDAS.OUVIR_DE_NOVO
      return this.comecouAFalar ? BOAS_VINDAS.CONTINUAR_FALA : BOAS_VINDAS.OUVIR
    },
    // O botão de fala some quando não há som e nunca houve: sem áudio, ele não teria o
    // que fazer, e um botão inerte sem motivo visível é pior que nenhum botão.
    mostrarBotaoDeFala() {
      return this.preparando || this.temAudio
    },
    /**
     * A marca "entregue" ocupa o lugar que era da ação primária.
     *
     * ⚠️ NÃO aparece enquanto o "Continuar" está na tela: os dois juntos dizem
     * coisas opostas — um fecha a mensagem ("fica aqui para consulta") e o
     * outro pede o próximo passo. Com `sem-audio` a entrega é registrada já na
     * montagem, então sem esta guarda a linha nascia junto com o botão (achado
     * do owner, 18/09/2026).
     */
    mostrarMarcaDeEntregue() {
      return this.concluida && !this.mostrarBotaoDeAvancar
    },
    /**
     * O rodapé só existe quando tem morador.
     *
     * ⚠️ Rodapé vazio ainda cobra o `mt-4`: com a fala desligada e o
     * "Continuar" na linha do fecho (19/09/2026), os três somem ao mesmo tempo
     * e sobravam 16px de nada no pé do cartão.
     */
    mostrarRodape() {
      return (
        this.mostrarBotaoDeFala || this.mostrarMarcaDeEntregue || !!this.aviso
      )
    },
  },
  // `mounted` e não `created`: o roteiro precisa estar pintado ANTES de qualquer espera
  // de rede — é o que faz o AC-2.6 valer sem depender de a síntese responder.
  mounted() {
    this.prepararFala()
    // ⚠️ `resolverClipe()` NÃO é chamado aqui: quem o chama é o `mounted` do
    // mixin de mídia, e os dois hooks rodam (o Vue funde, não substitui) —
    // chamar nos dois lugares pedia o clipe DUAS vezes, e a busca dispara
    // geração paga no servidor.
    // Sem áudio por decisão do host, o desfecho já é conhecido: a mensagem é o texto.
    if (this.semAudio) this.reportarEntrega(CANAL_TEXTO)
  },
  // O áudio é um recurso do browser, não um dado: sem isto ele continuaria tocando depois
  // de o componente sair da tela.
  beforeDestroy() {
    this.encerrarAudio()
  },
  methods: {
    /** Avança a apresentação: quem escuta revela o próximo passo. */
    avancar() {
      if (this.avancou) return
      this.avancou = true
      this.$emit('avancar')
    },
    // Os blocos de um papel, na ordem do roteiro. Método e não computed por papel para
    // não repetir o mesmo `filter` seis vezes — quem o chama são os seis computeds acima,
    // nunca o template.
    doPapel(papel) {
      return this.estruturaDoRoteiro.filter((item) => item.papel === papel)
    },
    // ★ O clipe, quando há provedor. `obter()` nunca rejeita (a degradação é o contrato
    // dele), então não há `catch` a fazer aqui: gerando, indisponível e ausente devolvem
    // URL vazia, e a tela segue com o retrato — o mesmo caminho de quem nunca teve clipe.
    async prepararFala() {
      if (this.semAudio) return
      this.preparando = true
      const pedido = await this.pedidoDeSintese()
      if (this._destruido) return
      const r = await apiVoz.sintetizar(pedido)
      // O componente pode ter saído da tela enquanto a síntese vinha.
      if (this._destruido) return
      this.preparando = false
      const valor = (r && r.value) || null
      const base64 = valor && valor.audio_base64
      // Falha ou prazo estourado: o texto já está na tela, e é isso que o aviso
      // diz. Mesmo desfecho de um áudio que falha ao tocar — uma fonte só.
      if (!base64) return this.aoFalharAudio()
      this.montarAudio(base64, (valor && valor.content_type) || TIPO_PADRAO)
      this.tentarTocar()
    },
    /**
     * O corpo da síntese — e a decisão de QUAL áudio pedir.
     *
     * ⚠️ O TEXTO já está na tela desde o `mounted` (AC-2.6): o que espera
     * aqui é só a decisão, nunca a mensagem. Medido: a sessão do avatar abre
     * em 1,0-2,6 s com o sistema ocioso; passado o teto, a apresentação sai
     * em MP3 pelo `<audio>` de sempre.
     */
    async pedidoDeSintese() {
      const pedido = {
        texto: textoFaladoDoRoteiro(this.primeiroNome),
        voice_id: this.vozId || undefined,
      }
      if (!this.falante) return pedido
      // Só EXISTE quando é verdade — sem avatar, o corpo é o de hoje.
      if (await this.falante.prontoEm(TETO_ESPERAR_AVATAR_MS)) {
        pedido.paraAvatar = true
      }
      return pedido
    },
    montarAudio(base64, contentType) {
      // ★ O discriminante é o `contentType` que o SERVIDOR mandou, não a
      // existência do falante: a decisão de formato acontece antes da
      // síntese, e entre uma coisa e outra a sessão pode ter caído. Bytes de
      // MP3 entregues ao provedor fariam o avatar falar ruído, sem erro
      // nenhum no fio (mesma guarda de `fala-da-ia.js`).
      const audio =
        this.falante && contentType === CONTENT_TYPE_PCM
          ? criarFalanteComoAudio({
              pcmBase64: base64,
              falar: this.falante.falar,
              interromper: this.falante.interromper,
            })
          : new Audio(`data:${contentType};base64,${base64}`)
      audio.addEventListener('timeupdate', this.aoProgredir)
      audio.addEventListener('ended', this.aoTerminar)
      audio.addEventListener('error', this.aoFalharAudio)
      // Fora do `data()` de propósito: o elemento é um recurso, não estado de tela, e
      // deixá-lo reativo faria o Vue observar um objeto do browser inteiro a cada frame.
      this._audio = audio
      this.temAudio = true
    },
    // ★ O único lugar que chama `play()`. A recusa do autoplay vira CONVITE, não erro.
    tentarTocar() {
      if (!this._audio) return
      const promessa = this._audio.play()
      // Browsers antigos não devolvem promise no `play()`.
      if (!promessa || typeof promessa.then !== 'function') {
        this.tocando = true
        this.comecouAFalar = true
        this.reportarEntrega(CANAL_AUDIO)
        return
      }
      promessa.then(
        () => {
          this.tocando = true
          this.comecouAFalar = true
          this.aviso = ''
          this.reportarEntrega(CANAL_AUDIO)
        },
        () => {
          this.tocando = false
          this.aviso = BOAS_VINDAS.PRECISA_DE_GESTO
          // ★ E a entrega fecha por TEXTO. Antes quem a fechava era o clique em
          // "Continuar"; sem ele (owner, 18/09) ninguém fecharia, e a boas-vindas
          // voltaria na sessão seguinte como se nunca tivesse sido dada. O roteiro
          // está inteiro na tela desde o primeiro frame (AC-2.6), então `texto` é o
          // canal honesto: é assim que a pessoa recebeu a mensagem.
          this.reportarEntrega(CANAL_TEXTO)
        }
      )
    },
    alternarFala() {
      if (!this._audio) {
        // Sem áudio o botão não existe na tela; o guard é para o clique por teclado.
        return
      }
      if (this.tocando) {
        this.pausarFala()
        return
      }
      this.tentarTocar()
    },
    aoProgredir() {
      if (!this._audio) return
      const progresso = progressoDoAudio(
        this._audio.currentTime,
        this._audio.duration
      )
      this.blocoAtivo = blocoNoProgresso(this.pesos, progresso)
    },
    aoTerminar() {
      this.tocando = false
      // O último bloco fica aceso: a fala acabou nele, e apagar a legenda no fim daria a
      // impressão de que algo foi cortado.
      this.blocoAtivo = this.pesos.length - 1
      this.finalizar()
    },
    aoFalharAudio() {
      this.tocando = false
      this.aviso = BOAS_VINDAS.SEM_AUDIO
      this.reportarEntrega(CANAL_TEXTO)
    },
    // ★ O CANAL HONESTO (spec-009 [US-2]): como a mensagem de fato chegou a esta pessoa.
    // `audio` quando a fala COMEÇOU; `texto` quando não houve som — e também quando o
    // autoplay foi recusado e o consultor seguiu sem ouvir, que é o que aconteceu de
    // verdade. Sai UMA vez: é ele que o host manda para `apiBoasVindas.marcarEntregue`.
    reportarEntrega(canal) {
      if (this.canalReportado) return
      this.canalReportado = true
      this.$emit('entregue', { canal })
    },
    // "Continuar": o consultor decide seguir. Vale com áudio e sem áudio — é o caminho
    // explícito para o fim quando não há som para terminar sozinho.
    //
    /** Cala o que estiver tocando sem abrir mão do áudio já sintetizado. */
    pausarFala() {
      if (!this._audio) return
      this._audio.pause()
      this.tocando = false
    },
    // ★ `fim` é o gancho da US-3 (oferecer o modo de voz quando a boas-vindas termina), e
    // sai UMA vez só: o áudio terminando e o clique em "Continuar" são o mesmo desfecho.
    finalizar() {
      if (this.terminou) return
      this.terminou = true
      // Chegou ao fim sem nunca ter tocado (autoplay recusado e o consultor seguiu):
      // o canal honesto é o texto, que é como ele leu a mensagem.
      this.reportarEntrega(CANAL_TEXTO)
      this.$emit('fim')
    },
    encerrarAudio() {
      this._destruido = true
      if (!this._audio) return
      this._audio.pause()
      this._audio.removeEventListener('timeupdate', this.aoProgredir)
      this._audio.removeEventListener('ended', this.aoTerminar)
      this._audio.removeEventListener('error', this.aoFalharAudio)
      // O falante do avatar tem relógio próprio; um `<audio>` de verdade não
      // tem `encerrar` e cai no `if`.
      if (typeof this._audio.encerrar === 'function') this._audio.encerrar()
      this._audio = null
      this.temAudio = false
      this.tocando = false
    },
  },
}
</script>

<template>
  <article
    class="boas-vindas-falada"
    :class="classesDaMoldura"
    role="region"
    :aria-label="copy.ARIA"
    data-test="boas-vindas-falada"
  >
    <!-- Clipe OU retrato OU nada: os três são estados previstos. `muted` + `playsinline`
         porque o som é do TTS — o clipe é imagem em movimento, não fonte de áudio. -->
    <!-- ★ O ROSTO AO VIVO falando esta apresentação. `muted` porque o som vem
         da faixa de áudio da sala, logo abaixo — não deste elemento. -->
    <video
      v-if="temAoVivo"
      ref="video"
      class="boas-vindas-falada__ao-vivo d-block mb-4"
      autoplay
      muted
      playsinline
      data-test="boas-vindas-ao-vivo"
    />
    <video
      v-else-if="mostrarClipe"
      class="boas-vindas-falada__midia d-block mb-4"
      :src="clipeEfetivo"
      autoplay
      muted
      loop
      playsinline
      data-test="boas-vindas-clipe"
    />
    <img
      v-else-if="temRetrato"
      class="boas-vindas-falada__midia d-block mb-4"
      :src="retrato"
      :alt="copy.ARIA_RETRATO"
      data-test="boas-vindas-retrato"
    />
    <!-- A faixa de áudio da sala: é ela que faz som enquanto o avatar fala (o
         `<audio>` local não é montado nesse caminho). ⚠️ FORA da cadeia
         `v-if`/`v-else-if` acima: um `v-if` no meio dela ORFANIZA o
         `v-else-if` seguinte e o clipe deixa de renderizar, sem erro. -->
    <audio
      v-if="temAoVivo"
      ref="audio"
      autoplay
      data-test="boas-vindas-audio-da-sala"
    />

    <div
      class="boas-vindas-falada__roteiro"
      aria-live="polite"
      data-test="boas-vindas-roteiro"
    >
      <p
        v-if="saudacaoDoRoteiro"
        class="boas-vindas-falada__bloco boas-vindas-falada__saudacao ia-texto-hero mb-4"
        :class="{ 'boas-vindas-falada__bloco--ativo': saudacaoDoRoteiro.ativo }"
        :data-ativo="saudacaoDoRoteiro.dataAtivo"
        data-test="boas-vindas-bloco"
      >
        {{ saudacaoDoRoteiro.texto }}
      </p>

      <p
        v-for="item in paragrafosDoRoteiro"
        :key="item.indice"
        class="boas-vindas-falada__bloco boas-vindas-falada__paragrafo az-text-primary az-size-14 mb-4"
        :class="{ 'boas-vindas-falada__bloco--ativo': item.ativo }"
        :data-ativo="item.dataAtivo"
        data-test="boas-vindas-bloco"
      >
        {{ item.texto }}
      </p>

      <p
        v-if="rotuloDosPassos"
        class="boas-vindas-falada__bloco az-ds-label mb-3"
        :class="{ 'boas-vindas-falada__bloco--ativo': rotuloDosPassos.ativo }"
        :data-ativo="rotuloDosPassos.dataAtivo"
        data-test="boas-vindas-bloco"
      >
        {{ rotuloDosPassos.texto }}
      </p>

      <!-- Os quatro passos em grade 2x2 (uma coluna em tela estreita). -->
      <div
        v-if="passosDoRoteiro.length"
        class="boas-vindas-falada__passos az-gap"
      >
        <div
          v-for="item in passosDoRoteiro"
          :key="item.indice"
          class="boas-vindas-falada__bloco boas-vindas-falada__passo px-4"
          :class="{ 'boas-vindas-falada__bloco--ativo': item.ativo }"
          :data-ativo="item.dataAtivo"
          data-test="boas-vindas-bloco"
        >
          <BoasVindasPasso :passo="item.passo" />
        </div>
      </div>

      <div
        v-if="linhasDoRodape.length"
        class="boas-vindas-falada__divisoria mt-6 mb-4"
      />

      <p
        v-for="item in linhasDoRodape"
        :key="item.indice"
        class="boas-vindas-falada__bloco boas-vindas-falada__linha az-text-primary az-size-14 mb-2"
        :class="{ 'boas-vindas-falada__bloco--ativo': item.ativo }"
        :data-ativo="item.dataAtivo"
        data-test="boas-vindas-bloco"
      >
        <!-- ⚠️ As duas linhas são um PAR de itens da mesma lista: o `mb-2` (8px) acima é
             o espaço ENTRE elas, apertado de propósito (owner, 18/09/2026: *"mais
             coladas, mas não tanto"*). O respiro do grupo contra a divisória e contra o
             fecho não muda — ele vem do `mt-6` do fecho, que vence por colapso. -->
        <span
          class="boas-vindas-falada__marcador mt-2 mr-2"
          aria-hidden="true"
        />
        <!-- ★ O caminho de créditos é CLICÁVEL (owner, 18/09). Ler um caminho de menu e
             percorrê-lo à mão é atrito justo no momento em que a pessoa já decidiu
             contratar. Onde não há caminho, a linha é texto puro — o normal. -->
        <span v-if="item.destino"
          >{{ item.antes
          }}<router-link
            :to="item.destino"
            data-test="boas-vindas-link-creditos"
            >{{ item.link }}</router-link
          >{{ item.depois }}</span
        >
        <span v-else>{{ item.texto }}</span>
      </p>

      <!-- ★ O fecho e o "Continuar" dividem UMA linha (owner, 19/09/2026:
           *"deixe ele mais para cima na linha da última mensagem para reduzir
           tamanho do componente"*). Com o botão no rodapé próprio, o cartão
           pagava 16px de margem mais a altura dele só para repetir, embaixo, o
           passo que a última frase já pede.
           ⚠️ `align-end` e não `align-center`: o fecho quebra em duas linhas no
           cartão estreito, e é na ÚLTIMA delas que o botão tem de assentar —
           centrado, ele flutuaria no meio do parágrafo.
           ⚠️ O `mt-8` (32px, o passo que o DS usa entre seções) mudou de dono:
           era do parágrafo, agora é da LINHA. Deixá-lo no parágrafo somaria
           8px ao respiro — margem de item de flex não colapsa com a do irmão
           anterior (o `mb-2` da última linha do rodapé), e o fecho descia para
           40px do bloco acima. O que o distingue continua sendo só o espaço:
           a tipografia é a dos parágrafos do topo, sem modificador nenhum. -->
      <div
        v-if="fechamentoDoRoteiro || mostrarBotaoDeAvancar"
        class="boas-vindas-falada__fecho d-flex flex-wrap align-end az-gap mt-8"
      >
        <p
          v-if="fechamentoDoRoteiro"
          class="boas-vindas-falada__bloco boas-vindas-falada__fechamento az-text-primary az-size-14 mb-0"
          :class="{
            'boas-vindas-falada__bloco--ativo': fechamentoDoRoteiro.ativo,
          }"
          :data-ativo="fechamentoDoRoteiro.dataAtivo"
          data-test="boas-vindas-bloco"
        >
          {{ fechamentoDoRoteiro.texto }}
        </p>
        <!-- ★ O "Continuar" revela o formulário de perguntas — sem ele, a
             apresentação e o formulário chegam juntos e a tela deixa de ler
             como etapas. -->
        <BaseButton
          v-if="mostrarBotaoDeAvancar"
          primary
          fit-content
          class="ml-auto flex-shrink-0"
          data-test="boas-vindas-avancar"
          @click="avancar"
        >
          {{ copy.AVANCAR }}
        </BaseButton>
      </div>
    </div>

    <!-- ⚠️ Sem `v-if` o rodapé VAZIO ainda cobra o `mt-4`: com a fala desligada
         (`FALA_DO_PRIMEIRO_CONTATO = false`) e o "Continuar" agora na linha do
         fecho, os três moradores somem e sobravam 16px de nada no pé do cartão
         — justamente a altura que esta mudança foi buscar. -->
    <footer
      v-if="mostrarRodape"
      class="boas-vindas-falada__acoes d-flex align-center az-gap mt-4"
    >
      <BaseButton
        v-if="mostrarBotaoDeFala"
        outline
        fit-content
        :loading="preparando"
        data-test="boas-vindas-ouvir"
        @click="alternarFala"
      >
        {{ rotuloDaFala }}
      </BaseButton>
      <!-- ★ Entregue: no lugar da ação primária entra a MARCA de que a mensagem já foi
           recebida. Ela é o que explica o bloco continuar no alto da conversa — sem isso,
           um card sem ação nenhuma ali em cima parece resto de tela. -->
      <!-- ⚠️ Não aparece enquanto o "Continuar" está na tela: os dois juntos
           dizem coisas opostas — um fecha a mensagem ("fica aqui para
           consulta") e o outro pede o próximo passo. Com `sem-audio` a
           entrega é registrada já na montagem, então sem esta guarda a linha
           nascia junto com o botão (achado do owner, 18/09/2026). -->
      <span
        v-if="mostrarMarcaDeEntregue"
        class="boas-vindas-falada__entregue d-flex align-center az-gap-small az-ds-aux"
        data-test="boas-vindas-entregue"
      >
        <icon-svg name="check-circle" :size="14" color="var(--primary)" />
        {{ copy.ENTREGUE }}
      </span>
      <!-- O aviso do áudio é auxiliar: a mensagem continua inteira acima dele. -->
      <v-fade-transition>
        <span
          v-if="aviso"
          class="az-ds-aux"
          role="status"
          data-test="boas-vindas-aviso"
        >
          {{ aviso }}
        </span>
      </v-fade-transition>
    </footer>
  </article>
</template>

<style lang="sass" scoped>
// O padrão de 16px menos 2px, pedido com a tela na frente (owner, 18/09/2026). Fica em
// variável — e não solto na regra — porque não é um número qualquer: é o `pa-4` do
// template com um desconto declarado, e a escala do Vuetify não tem o passo de 14px.
$respiro-vertical-do-passo: 14px

.boas-vindas-falada__ao-vivo
  // Maior que o clipe: durante a apresentação o rosto É o assunto, e é isso
  // que o owner precisa ver para julgar a experiência.
  width: 200px
  height: 200px
  object-fit: cover
  border-radius: var(--radius-lg)
  background-color: var(--neutral-900)

// ★ EXPERIMENTO (prop `semMoldura`): o cartão vira texto corrido no corpo da
// conversa. Aditivo de propósito — some daqui e o default não muda.
.boas-vindas-falada--sem-moldura
  background: none
  border: 0
  box-shadow: none
  padding: 0

  // Sem o fundo do cartão, os passos sumiriam contra o branco: eles invertem o
  // contraste — fundo branco com hairline, em vez do `--neutral-50` de hoje.
  .boas-vindas-falada__passo
    background-color: var(--white)
    border: 1px solid var(--border-hairline)

.boas-vindas-falada
  &__midia
    // `display: block` vem do `d-block` no template (utilitário do Vuetify).
    width: 100%
    max-height: 220px
    object-fit: cover
    border-radius: var(--radius-md)

  &__paragrafo
    // Entrelinha confortável: no `az-size-14` puro os dois parágrafos de introdução
    // vinham apertados, que é o que o owner viu como "só texto".
    line-height: 1.6

  &__passos
    // O espaço entre os cartões é o `az-gap` do template (16px) — `gap` vale em grade
    // igual vale em flex, e não há utilitário de grade para pedir.
    display: grid
    grid-template-columns: repeat(2, 1fr)

    // Em coluna estreita a grade vira uma coluna: dois cartões de ~150px quebrariam o
    // título do passo em três linhas.
    @media (max-width: 599px)
      grid-template-columns: 1fr

  &__passo
    // Superfície sutil sobre o branco do cartão. O horizontal é o `px-4` do template; o
    // VERTICAL é o padrão de 16px menos 2px (owner, 18/09/2026), e 14px não tem
    // utilitário — a escala do Vuetify anda de 4 em 4.
    //
    // ⚠️ A faixa vazia que ele viu NÃO era o padding: era o `margin-bottom: 16px` que o
    // Vuetify dá a todo `<p>` — a descrição carregava 16px invisíveis embaixo dela. Quem
    // resolveu foi o `mb-0` em `boas-vindas-passo.vue`, junto com o número e o título
    // passando a dividir UMA linha (some uma linha inteira por cartão).
    // A grade continua igualando a altura dos quatro (`stretch`): é ela que faz a grade
    // parecer grade, e o que encolhe é o espaço INTERNO, não o alinhamento.
    padding-top: $respiro-vertical-do-passo
    padding-bottom: $respiro-vertical-do-passo
    background-color: var(--neutral-50)
    border-radius: var(--radius-lg)

  &__divisoria
    border-top: 1px solid var(--border-hairline)

  &__linha
    display: flex
    align-items: flex-start
    line-height: 1.6

  &__marcador
    // ★ Marcador, não estado: `--primary` aqui é decoração de lista. Se a marca do
    // escritório for clara e ele sumir, a linha continua legível por inteiro.
    // O respiro é `mt-2`/`mr-2` no template — 8px desce o ponto até o meio da primeira
    // linha (14px × 1.6). Aqui fica só a forma.
    flex: 0 0 auto
    width: 6px
    height: 6px
    border-radius: 999px
    background-color: var(--primary)

  &__fechamento
    // ⚠️ O fecho é corpo COMUM — `az-text-primary az-size-14`, exatamente os parágrafos
    // do topo. Sem serif, sem tamanho próprio e SEM PESO: as três foram tentadas com o
    // owner olhando a tela (18/09/2026) e recusadas, nesta ordem. O que o separa do bloco
    // acima é só ESPAÇO: o `mt-8` (32px) do template, o passo que o DS usa entre seções.
    // Precisando de mais ar, o ajuste é o `mt`, nunca a tipografia.
    // Aqui fica apenas a entrelinha, a mesma dos parágrafos.
    line-height: 1.6

  // A saudação é o papel `hero` (`.ia-texto-hero`, `design/_ia-texto.sass`) no
  // template; aqui só a COR, que é do elemento. ⚠️ Nunca no `&__bloco`: ele
  // embrulha os parágrafos (`az-text-primary`) e o rótulo (`az-ds-label`), e a
  // classe do scoped (0,2,0) venceria a cor das utilitárias.
  &__saudacao
    color: var(--neutral-900)

  &__bloco
    // A transição é do REALCE, não do texto: o bloco nunca aparece nem some — ele já
    // está lido antes de ser falado.
    transition: background-color 0.25s ease, color 0.25s ease

    &--ativo
      // Tingimento da marca, o mesmo peso do estado selecionado dos cards de plano.
      background-color: color-mix(in srgb, var(--primary) 8%, transparent)
      border-radius: var(--radius-sm)
      // O realce sangra para além do texto sem mexer no fluxo (o `mb-3` continua valendo).
      box-shadow: 0 0 0 6px color-mix(in srgb, var(--primary) 8%, transparent)
</style>
